import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  pushSupported,
  pushDismissed,
  dismissPush,
  subscribeToPush,
  currentPermission,
} from "@/lib/push";
import { Bell, X } from "lucide-react";

export function PushOptIn() {
  const { user, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (!pushSupported()) return;
    const perm = currentPermission();
    if (perm !== "default") return; // already granted or denied
    if (pushDismissed()) return;
    // Small delay so user isn't blitzed at load
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, [user, loading]);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifications on. We'll only ping you when it matters.");
      setVisible(false);
    } catch (e) {
      if (String(e.message).includes("Permission not granted")) {
        toast.error("Blocked. You can enable in browser settings later.");
      } else {
        toast.error(e.message || "Couldn't enable notifications");
      }
      setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  const later = () => {
    dismissPush();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-optin-banner"
      className="fixed top-4 right-4 left-4 md:left-auto md:max-w-sm z-50 bg-[#131518] border border-[#D5FF3F]/40 rounded-lg p-4 shadow-2xl fade-in"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-[#D5FF3F]/15 flex items-center justify-center flex-shrink-0">
          <Bell size={18} className="text-[#D5FF3F]" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[#F5F3EE] text-sm">Get pinged on wins</div>
          <p className="text-xs text-[#A8ADA5] mt-1 leading-relaxed">
            We'll notify you when a dispute resolves, a scan finishes, or your renewal is up. No spam.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={enable}
              disabled={busy}
              data-testid="push-enable-btn"
              className="rounded-full btn-lime h-8 px-4 text-xs"
            >
              {busy ? "Enabling…" : "Turn on"}
            </Button>
            <Button
              onClick={later}
              data-testid="push-later-btn"
              className="rounded-full btn-outline h-8 px-4 text-xs"
            >
              Later
            </Button>
          </div>
        </div>
        <button
          onClick={later}
          data-testid="push-close-btn"
          className="text-[#6D716C] hover:text-[#F5F3EE] p-1"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
